import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import AppFooter from '../components/AppFooter';

export default function TermsPage() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="terms-page">
      {/* Header */}
      <div className="bg-white border-b border-beige">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded-lg transition-all"
            data-testid="terms-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-sage" />
            <span className="text-lg font-medium text-charcoal">Terms of Service</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl border border-beige p-6 sm:p-10 shadow-sm">
            <h1 className="text-2xl sm:text-3xl font-light text-charcoal mb-1">
              Kura Stash Terms of Service
            </h1>
            <p className="text-sm text-gray-soft mb-8">Last Updated: March 6, 2026</p>

            <div className="space-y-8 text-charcoal leading-relaxed">
              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">1. Acceptance of Terms</h2>
                <p className="text-sm text-charcoal/80">
                  By accessing or using Kura Stash (<a href="https://kurastash.com" className="text-sage hover:text-sage-dark underline">kurastash.com</a>), a product of Kura Studio, you agree to be bound by these Terms of Service. If you do not agree, please do not use the service.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">2. Description of Service</h2>
                <p className="text-sm text-charcoal/80">
                  Kura Stash is a web application that helps paper crafters catalog and organize their supply inventory. The service includes AI-powered design suggestions, image storage, and community features. We reserve the right to modify, suspend, or discontinue any part of the service at any time.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">3. User Accounts</h2>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Registration</p>
                    <p className="text-sm text-charcoal/80">You must sign in using a valid Google or Facebook account. You are responsible for maintaining the security of your account credentials.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Accuracy</p>
                    <p className="text-sm text-charcoal/80">You agree to provide accurate information and to keep it up to date. We reserve the right to suspend accounts that violate these terms.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Account Termination</p>
                    <p className="text-sm text-charcoal/80">You may delete your account at any time through the app settings. We may terminate or suspend access to accounts that violate these terms or for any other reason at our discretion.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">4. Subscriptions &amp; Billing</h2>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Free Tier</p>
                    <p className="text-sm text-charcoal/80">Every user receives a free grant of item slots. This grant is non-transferable and may be subject to change.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Paid Subscriptions</p>
                    <p className="text-sm text-charcoal/80">Paid plans are billed monthly through Stripe. Prices are displayed before purchase. You may upgrade, downgrade, or cancel at any time. Downgrades take effect at the end of the current billing period.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Refunds</p>
                    <p className="text-sm text-charcoal/80">We do not offer automatic refunds for partial billing periods. For billing concerns, please contact us at <a href="mailto:support@kurastash.com" className="text-sage hover:text-sage-dark underline">support@kurastash.com</a>.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">5. User Content</h2>
                <div className="space-y-3">
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Ownership</p>
                    <p className="text-sm text-charcoal/80">You retain ownership of all content you upload (photos, descriptions, etc.). By uploading content, you grant Kura Stash a limited license to store, display, and process it for the purpose of providing the service.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Community Sharing</p>
                    <p className="text-sm text-charcoal/80">If you opt in to community sharing features, your shared content may be visible to other users. You can revoke sharing consent at any time in your settings.</p>
                  </div>
                  <div className="pl-4 border-l-2 border-sage/30">
                    <p className="text-sm font-medium text-charcoal">Prohibited Content</p>
                    <p className="text-sm text-charcoal/80">You agree not to upload content that is illegal, harmful, or infringes on the rights of others. We reserve the right to remove such content without notice.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">6. AI Features</h2>
                <p className="text-sm text-charcoal/80">
                  Kura Stash uses artificial intelligence to provide design suggestions and image analysis. AI-generated content is provided "as-is" for inspiration purposes and may not always be accurate. We are not liable for decisions made based on AI suggestions.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">7. Limitation of Liability</h2>
                <p className="text-sm text-charcoal/80">
                  Kura Stash is provided "as is" without warranties of any kind. To the fullest extent permitted by law, Kura Studio shall not be liable for any indirect, incidental, or consequential damages arising from your use of the service, including but not limited to loss of data or interruption of service.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">8. Changes to Terms</h2>
                <p className="text-sm text-charcoal/80">
                  We may update these Terms of Service from time to time. Changes will be posted on this page with an updated revision date. Continued use of the service after changes constitutes acceptance of the new terms.
                </p>
              </section>

              <section>
                <h2 className="text-base font-medium text-charcoal mb-2">9. Contact Us</h2>
                <p className="text-sm text-charcoal/80">
                  For questions about these terms, please contact us at{' '}
                  <a href="mailto:support@kurastash.com" className="text-sage hover:text-sage-dark underline">
                    support@kurastash.com
                  </a>.
                </p>
              </section>
            </div>
          </div>
        </article>
      </div>

      <AppFooter variant="minimal" />
    </div>
  );
}
